import React, { useEffect, useState, useRef } from 'react';
import { AuthUser } from '../utils/authApi';
import { getCurrencySymbol } from '../utils/currency';
const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface OutletStat {
  outlet_id: number;
  outlet_name: string;
  city: string | null;
  currency: string;
  month_revenue: number;
  month_orders: number;
  today_revenue: number;
  today_orders: number;
  active_tokens: number;
}

interface DashboardData {
  outlets: OutletStat[];
  total_month_revenue: number;
  total_today_revenue: number;
  total_outlets: number;
}

function groupOutletsByCurrency(outlets: OutletStat[]): Record<string, OutletStat[]> {
  const map: Record<string, OutletStat[]> = {};
  outlets.forEach(o => {
    const cur = o.currency || 'USD';
    if (!map[cur]) map[cur] = [];
    map[cur].push(o);
  });
  return map;
}

export default function SuperAdminDashboard({ authUser }: { authUser: AuthUser | null }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const chartRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pieCanvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});
  const pieChartRefs = useRef<Record<string, any>>({});
const currSym = getCurrencySymbol(localStorage.getItem('cloudairambo_active_currency') || 'INR');
  const load = () => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    setLoading(true); setError('');
    fetch(`${BASE}/outlets/dashboard`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Failed to load dashboard'); setLoading(false); });
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    import('chart.js').then((ChartModule) => {
  const { Chart, BarController, CategoryScale, LinearScale, BarElement, Tooltip } = ChartModule;
  Chart.register(BarController, CategoryScale, LinearScale, BarElement, Tooltip);
  const Chart_ = Chart;
      if (chartRef.current) chartRef.current.destroy();
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
      const textColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)';
      const colors = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444'];
      chartRef.current = new Chart(canvasRef.current!, {
        type: 'bar',
        data: {
          labels: ['Jan','Feb','Mar','Apr','May','Jun'],
          datasets: data.outlets.map((o, i) => ({
            label: o.outlet_name,
            data: [0,0,0,0,0, o.month_revenue],
            backgroundColor: colors[i % colors.length],
            borderRadius: 4,
            borderSkipped: false,
          }))
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => ` ${currSym}${ctx.parsed.y.toLocaleString('en-IN')}` } } },
          scales: {
            x: { grid: { color: gridColor }, ticks: { color: textColor, font: { size: 11 } }, border: { display: false } },
            y: { grid: { color: gridColor }, ticks: { color: textColor, font: { size: 11 }, callback: (v: any) => `${currSym}${v}` }, border: { display: false }, beginAtZero: true }
          }
        }
      });
    }).catch(() => {});
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [data]);

  useEffect(() => {
    if (!data) return;
    import('chart.js').then((ChartModule) => {
      const { Chart, ArcElement, DoughnutController, Tooltip } = ChartModule;
      Chart.register(ArcElement, DoughnutController, Tooltip);
      const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];
      const groups = groupOutletsByCurrency(data.outlets);
      Object.entries(groups).forEach(([currency, outletList]) => {
        const canvas = pieCanvasRefs.current[currency];
        if (!canvas) return;
        if (pieChartRefs.current[currency]) pieChartRefs.current[currency].destroy();
        pieChartRefs.current[currency] = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: outletList.map(o => o.outlet_name),
            datasets: [{
              data: outletList.map(o => o.month_revenue),
              backgroundColor: outletList.map((_, i) => colors[i % colors.length]),
              borderWidth: 2,
              borderColor: '#fff',
              hoverOffset: 6,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '64%',
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: any) => ` ${ctx.label}: ${fmt(ctx.parsed, currency)}`,
                },
              },
            },
          },
        });
      });
    }).catch(() => {});
    return () => { Object.values(pieChartRefs.current).forEach(c => c?.destroy()); };
  }, [data]);

  const fmt = (n: number, currency = 'INR') => {
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
    catch { return `${currSym}${n}`; }
  };

  const topOutlet = data?.outlets.length
    ? data.outlets.reduce((a, b) => b.month_revenue > a.month_revenue ? b : a, data.outlets[0])
    : null;
  const maxRevenue = data ? Math.max(...data.outlets.map(o => o.month_revenue), 1) : 1;
  const colors = ['#3b82f6','#8b5cf6','#10b981','#f59e0b'];

  const s: Record<string, React.CSSProperties> = {
    shell: { display: 'flex', minHeight: 'calc(100vh - 64px)', background: 'var(--color-background-tertiary,#f8fafc)' },
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    topbar: { background: 'var(--color-background-primary,#fff)', borderBottom: '0.5px solid var(--color-border-tertiary,#e5e7eb)', padding: '0 1.5rem', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    content: { padding: '1.5rem', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' },
    breadcrumb: { fontSize: 13, color: 'var(--color-text-tertiary,#9ca3af)' },
    topRight: { display: 'flex', alignItems: 'center', gap: 10 },
    switcher: { display: 'flex', alignItems: 'center', gap: 6, border: '0.5px solid var(--color-border-tertiary,#e5e7eb)', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', background: 'var(--color-background-secondary,#f1f5f9)', color: 'var(--color-text-secondary,#64748b)' },
    avatar: { width: 32, height: 32, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 500, color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
    pageTitle: { fontSize: 20, fontWeight: 500 },
    pageSub: { fontSize: 13, color: 'var(--color-text-secondary,#64748b)', marginTop: 3 },
    refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, border: '0.5px solid var(--color-border-tertiary,#e5e7eb)', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', background: 'var(--color-background-primary,#fff)', color: 'var(--color-text-secondary,#6b7280)' },
    metrics: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 },
    metric: { background: 'var(--color-background-secondary,#f1f5f9)', borderRadius: 8, padding: '1rem 1.125rem' },
    metricLabel: { fontSize: 11, color: 'var(--color-text-tertiary,#9ca3af)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
    metricValue: { fontSize: 22, fontWeight: 500 },
    metricSub: { fontSize: 11, color: 'var(--color-text-tertiary,#9ca3af)', marginTop: 3 },
    sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' },
    sectionTitle: { fontSize: 14, fontWeight: 500 },
    sectionSub: { fontSize: 12, color: 'var(--color-text-tertiary,#9ca3af)', marginTop: 2 },
    outletGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    outletCard: (isTop: boolean): React.CSSProperties => ({ background: 'var(--color-background-primary,#fff)', border: isTop ? '1.5px solid #3b82f6' : '0.5px solid var(--color-border-tertiary,#e5e7eb)', borderRadius: 12, padding: '1.125rem 1.25rem' }),
    ocHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.875rem' },
    ocIcon: { width: 36, height: 36, borderRadius: 8, background: 'var(--color-background-info,#eff6ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 },
    ocName: { fontSize: 14, fontWeight: 500 },
    ocCity: { fontSize: 12, color: 'var(--color-text-tertiary,#9ca3af)', marginTop: 1 },
    ocStats: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: '0.875rem' },
    ocStat: { background: 'var(--color-background-secondary,#f8fafc)', borderRadius: 8, padding: '0.625rem 0.75rem' },
    ocStatLabel: { fontSize: 10, color: 'var(--color-text-tertiary,#9ca3af)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 },
    ocStatVal: { fontSize: 15, fontWeight: 500 },
    ocStatSub: { fontSize: 10, color: 'var(--color-text-tertiary,#9ca3af)', marginTop: 1 },
    barTrack: { height: 4, background: 'var(--color-background-secondary,#f1f5f9)', borderRadius: 99, overflow: 'hidden' },
    chartRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    chartCard: { background: 'var(--color-background-primary,#fff)', border: '0.5px solid var(--color-border-tertiary,#e5e7eb)', borderRadius: 12, padding: '1.125rem 1.25rem' },
    legend: { display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: '0.75rem' },
    legendItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--color-text-secondary,#6b7280)' },
    tokenRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--color-background-secondary,#f8fafc)', borderRadius: 8, marginBottom: 8 },
    badge: (top: boolean): React.CSSProperties => ({ fontSize: 10, padding: '3px 8px', borderRadius: 99, fontWeight: 500, whiteSpace: 'nowrap', background: top ? 'var(--color-background-info,#eff6ff)' : 'var(--color-background-success,#f0fdf4)', color: top ? 'var(--color-text-info,#1d4ed8)' : 'var(--color-text-success,#166534)' }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: 'var(--color-text-secondary,#6b7280)', fontSize: 14 }}>Loading dashboard…</div>;
  if (error || !data) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
      <div style={{ color: '#ef4444', fontSize: 14 }}>{error || 'No data'}</div>
      <button onClick={load} style={s.refreshBtn}>↻ Retry</button>
    </div>
  );

  return (
    <>
    <style>{`
  .sad-content {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .sad-content::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    display: none;
  }
  @media (max-width: 768px) {
    .sad-metrics { grid-template-columns: repeat(2,1fr) !important; }
    .sad-outlet-grid { grid-template-columns: 1fr !important; }
    .sad-chart-row { grid-template-columns: 1fr !important; }
    .sad-pie-row { grid-template-columns: 1fr !important; }
    .sad-page-header { flex-wrap: wrap; gap: 10px; }
  }
`}</style>
    <div style={s.shell}>
      <div style={s.main}>
        <div style={s.topbar}>
          <span style={s.breadcrumb}>Admin / <strong style={{ color: 'var(--color-text-primary,#111827)', fontWeight: 500 }}>Dashboard</strong></span>
          <div style={s.topRight}>
            <div style={s.switcher}>🏪 All branches ▾</div>
            <div style={s.avatar}>{authUser?.name?.charAt(0).toUpperCase() || 'O'}</div>
          </div>
        </div>

        <div style={s.content} className="sad-content">
          <div style={s.pageHeader} className="sad-page-header">
            <div>
              <div style={s.pageTitle}>Owner dashboard</div>
              <div style={s.pageSub}>Live snapshot across all {data.total_outlets} outlet{data.total_outlets !== 1 ? 's' : ''} · {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            </div>
            <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
          </div>

          <div style={s.metrics} className="sad-metrics">
            {(() => {
              const groupSums = (field: 'today_revenue' | 'month_revenue') => {
                const map: Record<string, number> = {};
                data.outlets.forEach(o => { map[o.currency] = (map[o.currency] || 0) + o[field]; });
                return map;
              };
              const todayByCur = groupSums('today_revenue');
              const monthByCur = groupSums('month_revenue');
              const renderMulti = (byCur: Record<string, number>) => {
                const entries = Object.entries(byCur);
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {entries.length === 0
                      ? <span style={{ fontSize: 22, fontWeight: 500 }}>{fmt(0)}</span>
                      : entries.map(([cur, amt]) => (
                        <span key={cur} style={{ fontSize: entries.length > 1 ? 16 : 22, fontWeight: 500 }}>
                          {fmt(amt, cur)}
                        </span>
                      ))}
                  </div>
                );
              };
              return [
                { label: "Today's revenue", node: renderMulti(todayByCur), sub: 'Per currency, all outlets' },
                { label: 'This month', node: renderMulti(monthByCur), sub: 'Per currency, all outlets' },
                { label: 'Total outlets', node: <div style={s.metricValue}>{data.total_outlets}</div>, sub: 'Active branches' },
                {
                  label: 'Top branch',
                  node: <div style={{ ...s.metricValue, fontSize: 16, paddingTop: 4 }}>{topOutlet?.outlet_name || '—'}</div>,
                  sub: topOutlet ? fmt(topOutlet.month_revenue, topOutlet.currency) + ' this month' : '',
                },
              ].map((m, i) => (
                <div key={i} style={s.metric}>
                  <div style={s.metricLabel}>{m.label}</div>
                  {m.node}
                  <div style={s.metricSub}>{m.sub}</div>
                </div>
              ));
            })()}
          </div>

          <div>
            <div style={s.sectionHeader}>
              <div>
                <div style={s.sectionTitle}>Outlet performance</div>
                <div style={s.sectionSub}>Revenue, orders, and token queue per branch</div>
              </div>
            </div>
            <div style={s.outletGrid} className="sad-outlet-grid">
              {data.outlets.map((o, i) => {
                const isTop = o.outlet_id === topOutlet?.outlet_id;
                const barPct = (o.month_revenue / maxRevenue) * 100;
                return (
                  <div key={o.outlet_id} style={s.outletCard(isTop)}>
                    <div style={s.ocHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={s.ocIcon}>🏪</div>
                        <div>
                          <div style={s.ocName}>{o.outlet_name}</div>
                          {o.city && <div style={s.ocCity}>📍 {o.city}</div>}
                        </div>
                      </div>
                      <span style={s.badge(isTop)}>{isTop ? 'Top branch' : 'Active'}</span>
                    </div>
                    <div style={s.ocStats}>
                      {[
                        { label: 'Today', val: fmt(o.today_revenue, o.currency), sub: `${o.today_orders} orders`, color: '#10b981' },
                        { label: 'This month', val: fmt(o.month_revenue, o.currency), sub: `${o.month_orders} orders`, color: '#3b82f6' },
                        { label: 'Waiting', val: String(o.active_tokens), sub: 'tokens', color: o.active_tokens > 0 ? '#f59e0b' : '#d1d5db' },
                      ].map((st, si) => (
                        <div key={si} style={s.ocStat}>
                          <div style={s.ocStatLabel}>{st.label}</div>
                          <div style={{ ...s.ocStatVal, color: st.color }}>{st.val}</div>
                          <div style={s.ocStatSub}>{st.sub}</div>
                        </div>
                      ))}
                    </div>
                    <div style={s.barTrack}>
                      <div style={{ height: '100%', borderRadius: 99, background: colors[i % colors.length], width: `${barPct}%`, transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(() => {
            const groups = groupOutletsByCurrency(data.outlets);
            const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];
            const entries = Object.entries(groups);
            if (entries.length === 0) return null;
            return (
              <div>
                <div style={s.sectionHeader}>
                  <div>
                    <div style={s.sectionTitle}>Revenue by outlet</div>
                    <div style={s.sectionSub}>This month's split, grouped by currency</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(entries.length, 3)}, 1fr)`, gap: 12 }} className="sad-pie-row">
                  {entries.map(([currency, outletList]) => {
                    const total = outletList.reduce((sum, o) => sum + o.month_revenue, 0);
                    return (
                      <div key={currency} style={s.chartCard}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={s.sectionTitle}>{currency}</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-tertiary,#9ca3af)' }}>{fmt(total, currency)}</div>
                        </div>
                        <div style={{ position: 'relative', width: '100%', height: 180, marginTop: 8 }}>
                          <canvas ref={el => { pieCanvasRefs.current[currency] = el; }} role="img" aria-label={`Revenue split by outlet in ${currency}`} />
                        </div>
                        <div style={s.legend}>
                          {outletList.map((o, i) => (
                            <span key={o.outlet_id} style={s.legendItem}>
                              <span style={{ width: 8, height: 8, borderRadius: 2, background: colors[i % colors.length], display: 'inline-block' }} />
                              {o.outlet_name} · {total > 0 ? Math.round((o.month_revenue / total) * 100) : 0}%
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div style={s.chartRow} className="sad-chart-row">
            <div style={s.chartCard}>
              <div style={s.sectionTitle}>Monthly revenue</div>
              <div style={s.sectionSub}>Last 6 months · all outlets</div>
              <div style={s.legend}>
                {data.outlets.map((o, i) => (
                  <span key={o.outlet_id} style={s.legendItem}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: colors[i % colors.length], display: 'inline-block' }} />
                    {o.outlet_name}
                  </span>
                ))}
              </div>
              <div style={{ position: 'relative', width: '100%', height: 160, marginTop: '0.875rem' }}>
                <canvas ref={canvasRef} role="img" aria-label="Monthly revenue bar chart per outlet" />
              </div>
            </div>
            <div style={s.chartCard}>
              <div style={s.sectionTitle}>Token queue</div>
              <div style={s.sectionSub}>Waiting tokens per outlet</div>
              <div style={{ marginTop: '0.875rem' }}>
                {data.outlets.map(o => (
                  <div key={o.outlet_id} style={s.tokenRow}>
                    <div>
                      <div style={{ fontSize: 13 }}>{o.outlet_name}</div>
                      {o.city && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary,#9ca3af)' }}>{o.city}</div>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: o.active_tokens > 0 ? '#f59e0b' : '#d1d5db' }} />
                      {o.active_tokens} waiting
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 8, padding: '0.75rem', background: 'var(--color-background-secondary,#f8fafc)', borderRadius: 8, fontSize: 12, color: 'var(--color-text-tertiary,#9ca3af)' }}>
                  Token counts update live when customers join the queue.
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
    </>
  );
}
